import { DefaultTFuncReturn } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useGetModelUserTasks } from '../../../api/tasks/tasks';
import { useDefaultOptions } from '../../../hooks/api';
import * as sessionStorageUtils from '../../../utils/sessionStorage';
import SlideToggle from '../../modules/SlideToggle';

// 一覧のソートキー
type SortKey =
  | 'sales_my'
  | 'model_name'
  | 'destination_name'
  | 'production_division_name'
  | 'production_no'
  | 'production_status_name'
  | 'due_date'
  | 'remaining_days'
  | 'release_on'
  | 'mass_production_on'
  | 'completion_rate'
  | 'model_user_name';

interface SortableThProps {
  label: DefaultTFuncReturn;
  onClick: () => void;
}

function SortableTh({ label, onClick }: SortableThProps) {
  return (
    <th>
      <button type="button" className="text-button" onClick={onClick}>
        {label}
        <i className="fa-solid fa-sort" />
      </button>
    </th>
  );
}

function ModelUserTaskList() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [open, setOpen] = useState(false);
  const options = useDefaultOptions(language);
  const modelUserTasks = useGetModelUserTasks(options);
  const handleOpen = () => {
    setOpen(!open);
  };

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStorageUtils.get('ModelUserTasks.SortInfo', { key: 'due_date' as SortKey, isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedTasks = useMemo(
    () =>
      [...(modelUserTasks.data?.data.model_user_tasks || [])].sort((a, b) => {
        const aAny = a as { [key: string]: string | undefined };
        const bAny = b as { [key: string]: string | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;
        if (aAny[sortInfo.key] !== bAny[sortInfo.key]) {
          // 第一ソートキー
          return ((aAny[sortInfo.key] ?? '') > (bAny[sortInfo.key] ?? '') ? 1 : -1) * multiValue;
        }
        return 1;
      }),
    [sortInfo, modelUserTasks.data?.data.model_user_tasks]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: SortKey) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: pre.key === key && !pre.isDisc };
      sessionStorageUtils.set('ModelUserTasks.SortInfo', next);
      return next;
    });
  }, []);

  // データが変更されたときの動作
  useEffect(() => {
    if (modelUserTasks.data?.data.model_user_tasks != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      }
    }
  }, [modelUserTasks.data?.data.model_user_tasks]);

  return (
    <>
      <h3 className="trig" onClick={handleOpen} style={{ display: 'inline-block' }}>
        <span>
          {t('機種担当')}
          <i className={`fa-solid ${open ? 'fa-chevron-down' : 'fa-chevron-up'}`} />
        </span>
      </h3>

      <SlideToggle open={open}>
        <div className="height_5l">
          <div className="wfr documentlist trigfr fropen">
            <table className="table1">
              <thead>
                <tr>
                  <th>
                    {t('link')}
                    <i className="fa-solid fa-sort" />
                  </th>
                  <SortableTh label={t('M/Y')} onClick={() => changeSortKey('sales_my')} />
                  <SortableTh label={t('機種名')} onClick={() => changeSortKey('model_name')} />
                  <SortableTh label={t('仕向')} onClick={() => changeSortKey('destination_name')} />
                  <SortableTh label={t('資料区分')} onClick={() => changeSortKey('production_division_name')} />
                  <SortableTh label={t('資料管理No')} onClick={() => changeSortKey('production_no')} />
                  <SortableTh label={t('ステータス')} onClick={() => changeSortKey('production_status_name')} />
                  <SortableTh label={t('期限')} onClick={() => changeSortKey('due_date')} />
                  <SortableTh label={t('残日数')} onClick={() => changeSortKey('remaining_days')} />
                  <SortableTh label={t('完成日')} onClick={() => changeSortKey('release_on')} />
                  <SortableTh label={t('量産日')} onClick={() => changeSortKey('mass_production_on')} />
                  <SortableTh label={t('原稿完成率')} onClick={() => changeSortKey('completion_rate')} />
                  <SortableTh label={t('機種担当')} onClick={() => changeSortKey('model_user_name')} />
                </tr>
              </thead>
              <tbody>
                {sortedTasks?.map((task) => (
                  <tr key={task.production_id}>
                    <td>
                      <Link to={`/${language}/productions/edit/${Number(task.production_id)}`}>
                        <i className="fa-solid fa-link" />
                      </Link>
                    </td>
                    <td>{task.sales_my}</td>
                    <td>{task.model_name}</td>
                    <td>{task.destination_name}</td>
                    <td>{task.production_division_name}</td>
                    <td>{task.production_no}</td>
                    <td>{task.production_status_name}</td>
                    <td>{task.due_date}</td>
                    <td>{task.remaining_days}</td>
                    <td>{task.release_on}</td>
                    <td>{task.mass_production_on}</td>
                    <td>
                      {task.completion_rate}
                      {task.completion_rate !== null ? '%' : ''}
                    </td>
                    <td>{task.model_user_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SlideToggle>
      <div style={{ marginBottom: '30px' }} />
    </>
  );
}
export { ModelUserTaskList };
